import React, { useEffect, useRef, useState } from 'react';
import { BellRing, Volume2, VolumeX } from 'lucide-react';
import { apiCall } from './_shared';

const ROLES = ['super_admin', 'admin', 'manager', 'reception', 'housekeeping', 'bar', 'waiter'];
const MUTE_KEY = 'rn_guest_alarm_muted_until';

// Three rising tones, louder and longer than the chat chime — this is an alarm.
export const alarmTone = () => {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const now = ctx.currentTime;
    [[660, 0], [880, 0.18], [1174, 0.36]].forEach(([freq, at]) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'square';
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.0001, now + at);
      gain.gain.exponentialRampToValueAtTime(0.25, now + at + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + at + 0.3);
      osc.connect(gain).connect(ctx.destination);
      osc.start(now + at);
      osc.stop(now + at + 0.32);
    });
    setTimeout(() => ctx.close().catch(() => {}), 1500);
  } catch { /* audio blocked until first interaction */ }
};

const ago = (iso) => {
  const m = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  return m < 1 ? 'just now' : m < 60 ? `${m} min ago` : `${Math.floor(m / 60)} h ago`;
};

const Item = ({ it }) => (
  <span className="inline-flex items-center gap-1 bg-white/15 rounded px-2 py-0.5 text-xs" data-testid={`guest-alarm-item-${it.id}`}>
    <b>{it.kind === 'order' ? 'Order' : 'Request'}</b> · {it.where} · {it.summary} <span className="opacity-70">· {ago(it.created_at)}</span>
  </span>
);

export const GuestAlarm = ({ userRole, onOpenTab }) => {
  const [data, setData] = useState(null);
  const [mutedUntil, setMutedUntil] = useState(() => Number(localStorage.getItem(MUTE_KEY) || 0));
  const seen = useRef(null);
  const allowed = ROLES.includes(userRole);

  useEffect(() => {
    if (!allowed) return undefined;
    let alive = true;
    const load = async () => {
      try { const d = await apiCall('/api/admin/inroom/alerts'); if (alive) setData(d); } catch { /* keep last */ }
    };
    load();
    const id = setInterval(load, 10000);
    return () => { alive = false; clearInterval(id); };
  }, [allowed]);

  const items = data?.items || [];
  const muted = Date.now() < mutedUntil;

  useEffect(() => {
    const ids = new Set(items.map((i) => i.id));
    if (seen.current === null) { seen.current = ids; if (ids.size && !muted) alarmTone(); return; }
    const fresh = [...ids].filter((i) => !seen.current.has(i));
    seen.current = ids;
    if (fresh.length && !muted) alarmTone();
  }, [items, muted]);

  useEffect(() => {
    if (!items.length || muted) return undefined;
    const id = setInterval(alarmTone, 30000);
    return () => clearInterval(id);
  }, [items.length, muted]);

  if (!allowed || !items.length) return null;
  const chips = items.slice(0, 3).map((it) => <Item key={it.id} it={it} />);
  const mute = () => { const until = Date.now() + 10 * 60000; localStorage.setItem(MUTE_KEY, String(until)); setMutedUntil(until); };
  const unmute = () => { localStorage.removeItem(MUTE_KEY); setMutedUntil(0); };
  return (
    <div className="bg-red-600 text-white px-3 sm:px-6 py-2 flex flex-wrap items-center gap-2 animate-pulse-slow" role="alert" data-testid="guest-alarm-banner">
      <BellRing className="w-4 h-4 animate-bounce" />
      <span className="text-sm font-bold" data-testid="guest-alarm-count">{data.orders_new} order{data.orders_new === 1 ? '' : 's'} · {data.requests_new} request{data.requests_new === 1 ? '' : 's'} waiting</span>
      {chips}
      {items.length > 3 && <span className="text-xs opacity-80">+{items.length - 3} more</span>}
      <span className="flex-1" />
      <button type="button" onClick={() => onOpenTab && onOpenTab('inroom')} className="text-xs font-semibold bg-white text-red-700 rounded px-2.5 py-1 hover:bg-red-50" data-testid="guest-alarm-open">Open In-Room</button>
      {muted
        ? <button type="button" onClick={unmute} className="text-xs inline-flex items-center gap-1 border border-white/50 rounded px-2 py-1" data-testid="guest-alarm-unmute"><VolumeX className="w-3.5 h-3.5" /> Muted</button>
        : <button type="button" onClick={mute} className="text-xs inline-flex items-center gap-1 border border-white/50 rounded px-2 py-1 hover:bg-white/10" data-testid="guest-alarm-mute"><Volume2 className="w-3.5 h-3.5" /> Mute 10 min</button>}
    </div>
  );
};
